import { isValid } from 'date-fns';
import { forkJoin, Observable, of } from 'rxjs';
import { mapTo, tap } from 'rxjs/operators';

import { catchErrorLogRet } from '../../../common/rest.operators';
import { CacheCriteriaParams } from '../../../insight/performance-data/cache-criteria-params.pd.model';
import { CacheCriteria } from '../../../insight/performance-data/cache-criteria.pd.model';
import {
    CriteriaIdType,
    PerformanceDataViewType,
} from '../../../insight/performance-data/metric.pd.model';
import { PerformanceDataService } from '../../../insight/performance-data/performance-data.service';
import { epicDependencies } from '../../../store';
import { SELECTED_ITEM_ALL_ID } from '../../../user/population-select';
import { ReportType } from '../../backend';
import { currentBuilderState } from '../builder.state';

export function loadCacheCriteriaOnCacheDateChange(): Observable<void> {
    return loadCacheCriteria(
        {
            dataViewTypeId: PerformanceDataViewType.USER,
            criteriaTypeId: CriteriaIdType.USER_ROLE_ID,
        },
        (ret) => {
            _loadCacheCriteria(CriteriaIdType.USER_ROLE_ID, ret);
        },
    );
}

export function loadCacheCriteriaOnReportTypeChange(): Observable<void> {
    const state = currentBuilderState();
    const vp = state.viewParams;

    switch (vp.reportType) {
        case ReportType.Role: {
            if (vp.role?.[0]?.id == null) {
                break;
            }
            const ccParams: CacheCriteriaParams = {
                dataViewTypeId: PerformanceDataViewType.USER,
                roleIds: [vp.role?.[0]?.id],
                criteriaTypeId: CriteriaIdType.SITE_ID,
            };
            return loadCacheCriteria(ccParams, (ret) => {
                _loadCacheCriteria(CriteriaIdType.SITE_ID, ret);
            });
        }
        case ReportType.Team: {
            if (vp.team?.[0]?.id == null) {
                break;
            }
            const ccParams: CacheCriteriaParams = {
                dataViewTypeId: PerformanceDataViewType.USER_QUEUE,
                managerIds: [vp.team?.[0]?.id],
            };
            return loadCacheCriteria(ccParams, (ret) => {
                _loadCacheCriteria(CriteriaIdType.QUEUE_ID, ret);
            });
        }
        case ReportType.Individual: {
            if (vp.individual?.[0]?.id == null) {
                break;
            }
            const ccParams: CacheCriteriaParams = {
                dataViewTypeId: PerformanceDataViewType.USER_QUEUE,
                userIds: [vp.individual?.[0]?.id],
            };
            return loadCacheCriteria(ccParams, (ret) => {
                _loadCacheCriteria(CriteriaIdType.QUEUE_ID, ret);
            });
        }
    }

    return of(null);
}

export function loadCacheCriteriaOnDimensionChange(
    wasSite: boolean,
): Observable<void> {
    if (!wasSite) {
        return of(null);
    }

    const state = currentBuilderState();
    const vp = state.viewParams;

    switch (vp.reportType) {
        case ReportType.Role: {
            if (vp.role?.[0]?.id == null || vp.site?.[0]?.id == null) {
                break;
            }
            return forkJoin([
                loadCacheCriteria(
                    {
                        dataViewTypeId: PerformanceDataViewType.USER_QUEUE,
                        roleIds: [vp.role?.[0]?.id],
                        siteIds:
                            vp.site?.[0]?.id === SELECTED_ITEM_ALL_ID
                                ? null
                                : [vp.site?.[0]?.id],
                    },
                    (ret) => {
                        _loadCacheCriteria(CriteriaIdType.QUEUE_ID, ret);
                    },
                ),
                loadCacheCriteria(
                    {
                        dataViewTypeId:
                            PerformanceDataViewType.USER_SUPER_GROUP,
                        roleIds: [vp.role?.[0]?.id],
                        siteIds:
                            vp.site?.[0]?.id === SELECTED_ITEM_ALL_ID
                                ? null
                                : [vp.site?.[0]?.id],
                    },
                    (ret) => {
                        _loadCacheCriteria(CriteriaIdType.SUPER_GROUP_ID, ret);
                    },
                ),
            ]).pipe(mapTo(null));
        }
    }

    return of(null);
}

function loadCacheCriteria(
    ccParams: CacheCriteriaParams,
    callback: (ret: CacheCriteria[]) => void,
): Observable<void> {
    const performanceDataService =
        epicDependencies.injector.get<PerformanceDataService>(
            PerformanceDataService,
        );

    const state = currentBuilderState();
    const vp = state.viewParams;

    if (vp.periodType?.id != null) {
        ccParams.periodTypeId = vp.periodType.id;
    }

    if (isValid(vp.cacheDate?.endDate)) {
        ccParams.eventDate = vp.cacheDate.endDate;
    }

    if (
        ccParams.dataViewTypeId == null ||
        ccParams.periodTypeId == null ||
        ccParams.eventDate == null
    ) {
        return of(null);
    }

    return performanceDataService.listCacheCriteria(ccParams).pipe(
        catchErrorLogRet([]),
        tap((ret) => {
            callback(ret);
        }),
        mapTo(null),
    );
}

function _loadCacheCriteria(
    criteriaTypeId: CriteriaIdType,
    ret: CacheCriteria[],
): void {
    const state = currentBuilderState();

    switch (criteriaTypeId) {
        case CriteriaIdType.USER_ROLE_ID:
            state.selectableRoleIds = ret.map((e) => e.criteriaId);
            break;
        case CriteriaIdType.SITE_ID:
            state.selectableSiteIds = ret.map((e) => e.criteriaId);
            state.selectableSiteIds.push(SELECTED_ITEM_ALL_ID);
            break;
        case CriteriaIdType.QUEUE_ID:
            state.selectableQueueIds = ret.map((e) => e.criteriaId);
            state.selectableQueueIds.push(SELECTED_ITEM_ALL_ID);
            break;
        case CriteriaIdType.SUPER_GROUP_ID:
            state.selectableSuperGroupIds = ret.map((e) => e.criteriaId);
            break;
    }
}
